import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import QuestionLearnPopup from './QuestionLearnPopup';

// Strip annotation markup like `[label]{CODE|type}` → keep the human label only.
const stripAnnotations = (text) =>
  (text || '').replace(/\[([^\]]+)\]\{[^}]+\}/g, '$1');

const truncate = (text, max = 140) => {
  const t = (text || '').trim();
  if (t.length <= max) return t;
  return t.slice(0, max).trimEnd() + '…';
};

const HoverTooltip = ({ rect, question }) => {
  if (!rect) return null;
  const explainVi = question?.teacher_note?.explain_vn;
  const explainEn = question?.teacher_note?.explain_en;
  const explainText = stripAnnotations(explainVi || explainEn);
  const tagCount = (question?.tag_details || []).length;

  // Position above icon center, with horizontal clamp to viewport.
  const TIP_WIDTH = 300;
  const ARROW_HALF = 6;
  const margin = 12;
  const centerX = rect.left + rect.width / 2;
  let left = centerX - TIP_WIDTH / 2;
  left = Math.max(margin, Math.min(left, window.innerWidth - TIP_WIDTH - margin));
  let top = rect.top - 12; // tooltip bottom sits 12px above icon
  let placeBelow = false;
  if (top < 80) {
    placeBelow = true;
    top = rect.bottom + 12;
  }

  return createPortal(
    <div
      style={{
        position: 'fixed',
        left,
        top,
        transform: placeBelow ? 'translateY(0)' : 'translateY(-100%)',
        width: TIP_WIDTH,
        maxWidth: 'calc(100vw - 24px)',
        background: '#2D2D2D',
        color: '#FFF',
        borderRadius: 10,
        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
        padding: '10px 12px',
        fontFamily: '"Nunito", "Baloo 2", sans-serif',
        fontSize: 13,
        lineHeight: 1.45,
        zIndex: 99998,
        pointerEvents: 'none',
        animation: 'qlb-tip-in 0.12s ease-out',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 800, color: '#FFD166', letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 4 }}>
        💡 Học từ câu này
      </div>
      {explainText && (
        <div style={{ marginBottom: tagCount ? 6 : 0 }}>
          {truncate(explainText, 160)}
        </div>
      )}
      {tagCount > 0 && (
        <div style={{ fontSize: 11, color: '#CFCFCF' }}>
          📚 {tagCount} từ vựng / cấu trúc — bấm để xem chi tiết
        </div>
      )}
      {!explainText && tagCount === 0 && (
        <div style={{ color: '#CFCFCF' }}>Bấm để xem chi tiết</div>
      )}
      <span
        style={{
          position: 'absolute',
          left: Math.max(ARROW_HALF + 4, Math.min(centerX - left, TIP_WIDTH - ARROW_HALF - 4)) - ARROW_HALF,
          [placeBelow ? 'top' : 'bottom']: -ARROW_HALF,
          width: 0, height: 0,
          borderLeft: `${ARROW_HALF}px solid transparent`,
          borderRight: `${ARROW_HALF}px solid transparent`,
          [placeBelow ? 'borderBottom' : 'borderTop']: `${ARROW_HALF}px solid #2D2D2D`,
        }}
      />
      <style>{`
        @keyframes qlb-tip-in {
          from { opacity: 0; transform: ${placeBelow ? 'translateY(-4px)' : 'translateY(calc(-100% + 4px))'}; }
          to   { opacity: 1; transform: ${placeBelow ? 'translateY(0)' : 'translateY(-100%)'}; }
        }
      `}</style>
    </div>,
    document.body
  );
};

const QuestionLearnButton = ({ question, size = 28 }) => {
  const [open, setOpen] = useState(false);
  const [hoverRect, setHoverRect] = useState(null);
  const btnRef = useRef(null);

  const hasTags = (question?.tag_details?.length || 0) > 0;
  const hasExplain = Boolean(question?.teacher_note?.explain_vn || question?.teacher_note?.explain_en);
  if (!hasTags && !hasExplain) return null;

  const handleEnter = (e) => {
    e.currentTarget.style.background = '#FFE9A8';
    e.currentTarget.style.transform = 'scale(1.08)';
    setHoverRect(btnRef.current?.getBoundingClientRect() || null);
  };
  const handleLeave = (e) => {
    e.currentTarget.style.background = '#FFF8E7';
    e.currentTarget.style.transform = 'scale(1)';
    setHoverRect(null);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); setHoverRect(null); }}
        aria-label="Xem giải thích & học thêm"
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: size, height: size, borderRadius: '50%',
          background: '#FFF8E7', color: '#8B6F47',
          border: '1px solid #E8E0CC',
          fontSize: Math.max(11, size - 10), fontWeight: 800,
          cursor: 'pointer', padding: 0, lineHeight: 1,
          flexShrink: 0,
          transition: 'transform 0.12s ease, background 0.12s ease',
        }}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={handleEnter}
        onBlur={handleLeave}
      >
        <span className="material-symbols-outlined" style={{ fontSize: Math.max(14, size - 6), fontVariationSettings: "'FILL' 1" }}>search</span>
      </button>
      {hoverRect && !open && <HoverTooltip rect={hoverRect} question={question} />}
      {open && <QuestionLearnPopup question={question} onClose={() => setOpen(false)} />}
    </>
  );
};

export default QuestionLearnButton;
